import React, { useState, useContext, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import Picker from "@emoji-mart/react";
import emojiData from "@emoji-mart/data";
import {
	Forward,
	Reply,
	Trash2,
	SmilePlus,
	Copy,
	Download,
	Image as ImageIcon,
	Info,
	Pencil,
} from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import ForwardMessageModal from "../ForwardMessageModal";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { useThemeContext } from "../../context/DarkMode";
import toastError from "../../errors/toastError";
import { copiarTexto as copiarParaAreaDeTransferencia } from "../../helpers/copiarTexto";

// Encaixa um flutuante (seletor completo de emoji, popover de informações)
// perto do botão que o abriu sem deixar sobrar para fora da tela: sobe em vez
// de descer quando não cabe embaixo, nunca passa da borda direita/esquerda, e
// devolve maxHeight (com scroll) para o caso raro de não caber em nenhum dos
// dois lados — um viewport baixo continua alcançando o conteúdo rolando.
const posicaoClampada = (rect, largura, altura, espaco = 4) => {
	if (!rect) return null;

	const espacoAbaixo = window.innerHeight - rect.bottom - espaco;
	const espacoAcima = rect.top - espaco;
	const abrirEmCima = espacoAbaixo < altura && espacoAcima > espacoAbaixo;
	const alturaMax = Math.min(altura, Math.max(abrirEmCima ? espacoAcima : espacoAbaixo, 120));

	return {
		top: abrirEmCima
			? Math.max(8, rect.top - alturaMax - espaco)
			: rect.bottom + espaco,
		left: Math.min(Math.max(8, rect.left), window.innerWidth - largura - 8),
		maxHeight: alturaMax,
		overflowY: "auto",
	};
};

// Os seis que cobrem quase toda reação no WhatsApp. Mais que isso vira uma
// fileira que não cabe na largura do menu — quem quiser outro emoji abre o
// seletor completo logo abaixo.
const RAPIDOS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const ACK_FALHA = -1;


const rotuloDoAck = (message) => {
	if (!message.fromMe) return "Recebida";
	if (message.ack === ACK_FALHA) return "Falha no envio";
	if (message.ack === 0) return "Enviando…";
	if (message.ack === 1) return "Enviada";
	if (message.ack === 2) return "Entregue";
	if (message.ack === 3 || message.ack === 4) return "Lida";
	return "—";
};

// Mantém a API antiga (anchorEl vindo do MessagesList) sem depender do MUI:
// o menu é posicionado a partir do retângulo do elemento âncora.
// `podeReagir` vem da capacidade da conexão do atendimento
// (`canal.capabilities.reactions`): Messenger e Direct não reagem, e o botão
// aqui só levaria a um 422. Padrão `true` — sem resposta do backend, nada some.
// `podeEditar` junta duas coisas decididas por quem abre o menu: a capacidade
// da conexão (`canal.capabilities.edicao` — a Cloud API da Meta não edita
// mensagem enviada, nem o sidecar de voz) e a janela de 15 minutos do
// WhatsApp, lida no clique. Padrão `true` — sem resposta do backend, nada
// some.
const MessageOptionsMenu = ({
	message,
	menuOpen,
	handleClose,
	anchorEl,
	podeReagir = true,
	podeEditar = true,
}) => {
	const { setReplyingMessage, setEditingMessage } =
		useContext(ReplyMessageContext);
	const { darkMode } = useThemeContext();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [encaminhar, setEncaminhar] = useState(false);
	// null = fechado; aberto guarda a posição já calculada (lida do rect do
	// botão no clique, nunca durante a renderização — refs não se lêem aí).
	const [seletorPos, setSeletorPos] = useState(null);
	const [infoPos, setInfoPos] = useState(null);
	const menuRef = useRef(null);
	const seletorRef = useRef(null);
	const infoRef = useRef(null);
	// Guarda o timer do foco adiado ao fechar o Encaminhar (ver mais abaixo)
	// para poder cancelá-lo — sem isto, desmontar o componente ou trocar de
	// atendimento antes dele rodar deixava o evento global focar o composer
	// do atendimento ERRADO.
	const timeoutFocoRef = useRef(null);

	useEffect(() => {
		return () => clearTimeout(timeoutFocoRef.current);
	}, []);
	// Só o retângulo do botão que abriu o menu, tirado NA abertura. Se
	// recalculássemos a cada render (como era antes), reabrir o seletor de
	// emoji ou o popover de informações re-renderiza este componente e, como
	// o botão-âncora só existe no hover da mensagem, o mouse já não está mais
	// lá — o rect vinha (0,0,0,0) e o menu pulava pro canto da tela.
	const [rect, setRect] = useState(null);

	useLayoutEffect(() => {
		// Medir o DOM (anchorEl) é o próprio propósito deste efeito, não um
		// derivado de estado/props que devesse ser calculado direto no render.
		if (menuOpen && anchorEl) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setRect(anchorEl.getBoundingClientRect());
		} else if (!menuOpen) {
			setRect(null);
		}
	}, [menuOpen, anchorEl]);

	// Fecha o menu por fora (clique fora ou Esc) e limpa o seletor/popover de
	// informações junto — senão reabrir noutra mensagem os herdaria abertos,
	// já que esta instância não desmonta entre uma abertura e outra.
	const fecharTudo = () => {
		setSeletorPos(null);
		setInfoPos(null);
		handleClose();
	};

	useEffect(() => {
		if (!menuOpen) return undefined;

		// Seletor de emoji e informações abrem em portais próprios (para não
		// serem cortados pelo overflow-hidden do menu, nem ficarem presos na
		// largura de 176px dele) — por isso o clique "de fora" também precisa
		// ignorar cliques dentro deles.
		const aoClicarFora = (e) => {
			const dentro =
				menuRef.current?.contains(e.target) ||
				seletorRef.current?.contains(e.target) ||
				infoRef.current?.contains(e.target);

			if (!dentro) fecharTudo();
		};
		const aoTeclar = (e) => e.key === "Escape" && fecharTudo();

		document.addEventListener("mousedown", aoClicarFora);
		document.addEventListener("keydown", aoTeclar);

		return () => {
			document.removeEventListener("mousedown", aoClicarFora);
			document.removeEventListener("keydown", aoTeclar);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuOpen, handleClose]);

	const apagarMensagem = async () => {
		try {
			await api.delete(`/messages/${message.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const responder = () => {
		setReplyingMessage(message);
		fecharTudo();
	};

	const reagir = async (emoji) => {
		fecharTudo();
		// O botão de emoji desmonta com o menu; sem isto o foco morre no body.
		document.dispatchEvent(new CustomEvent("composer:focar"));

		try {
			const { data } = await api.post(`/messages/${message.id}/reaction`, {
				emoji,
			});

			// A reação sempre fica salva aqui dentro (a equipe continua vendo),
			// mas em conexões sem essa capacidade (Cloud API, Messenger/Direct)
			// ela não chega no WhatsApp do cliente — sem avisar, a equipe achava
			// que tinha entregue.
			if (data?.entregue === false) {
				toast.info(
					"Reação salva, mas esta conexão não envia reação para o WhatsApp do cliente."
				);
			}
		} catch (err) {
			toastError(err);
		}
	};

	const copiarTexto = () => {
		fecharTudo();
		copiarParaAreaDeTransferencia(message.body, "Texto copiado.");
	};

	// mediaUrl aponta para o backend (outra origem do front em dev), e o
	// atributo `download` de um <a> é ignorado entre origens — o navegador
	// só abre outra aba. Baixando o arquivo como blob primeiro, o link vira
	// same-origin (blob:) e o download sai de verdade.
	const baixarMidia = async () => {
		fecharTudo();

		try {
			const resposta = await fetch(message.mediaUrl);
			const blob = await resposta.blob();
			const url = window.URL.createObjectURL(blob);

			const link = document.createElement("a");
			link.href = url;
			link.download = message.mediaUrl.split("/").pop() || "arquivo";
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch {
			toast.error("Não foi possível baixar o arquivo.");
		}
	};

	// O ClipboardItem tem que ser criado NA hora do clique, com a promise do
	// blob dentro dele — se esperássemos o fetch terminar antes de chamar
	// `clipboard.write`, alguns navegadores recusam por já ter perdido o
	// "gesto do usuário" que autoriza a escrita na área de transferência.
	// E sempre convertida para PNG: o tipo original (jpeg, webp…) nem sempre
	// é aceito pela Clipboard API, PNG é.
	const copiarImagem = () => {
		fecharTudo();

		try {
			const item = new window.ClipboardItem({
				"image/png": (async () => {
					const resposta = await fetch(message.mediaUrl);
					const blob = await resposta.blob();
					const bitmap = await createImageBitmap(blob);

					const canvas = document.createElement("canvas");
					canvas.width = bitmap.width;
					canvas.height = bitmap.height;
					canvas.getContext("2d").drawImage(bitmap, 0, 0);

					return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
				})(),
			});

			navigator.clipboard
				.write([item])
				.then(() => toast.success("Imagem copiada."))
				.catch(() => toast.error("Não foi possível copiar a imagem."));
		} catch {
			toast.error("Não foi possível copiar a imagem.");
		}
	};

	const abrirConfirmacao = () => {
		setConfirmationOpen(true);
		fecharTudo();
	};

	// mediaType vem do backend a partir do mimetype real do arquivo — mais
	// confiável que adivinhar pela extensão da URL (HEIC/AVIF ou link sem
	// extensão nenhuma também são imagem, e o MessagesList já usa este campo).
	const ehImagem = message?.mediaUrl && message.mediaType === "image";
	const podeCopiarImagem =
		ehImagem && navigator.clipboard?.write && window.ClipboardItem;

	return (
		<>
			<ForwardMessageModal
				open={encaminhar}
				onClose={() => {
					setEncaminhar(false);
					// O item de menu que abriu o modal já desmontou; sem isto o
					// foco morre no body em vez de voltar para quem escreve.
					// setTimeout, não direto: o Dialog do Radix trava o foco
					// dentro de si enquanto ainda está montado (FocusScope), e
					// despachar no mesmo tick faz esse guarda puxar o foco de
					// volta antes do modal sair do DOM. Um tick depois ele já
					// saiu, e o foco vai para quem escreve de verdade.
					clearTimeout(timeoutFocoRef.current);
					timeoutFocoRef.current = setTimeout(() => {
						document.dispatchEvent(new CustomEvent("composer:focar"));
					}, 0);
				}}
				message={message}
			/>
			<ConfirmationModal
				title={i18n.t("messageOptionsMenu.confirmationModal.title")}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={apagarMensagem}
			>
				{i18n.t("messageOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>

			{menuOpen &&
				rect &&
				createPortal(
					<div
						ref={menuRef}
						role="menu"
						style={{
							position: "fixed",
							top: rect.bottom + 4,
							// Alinha a borda direita do menu com a do botão.
							left: Math.max(8, rect.right - 208),
							// O menu cresceu (8 ações agora) e sempre abre para baixo; perto
							// do rodapé da tela ele não caberia inteiro. Em vez de virar para
							// cima (mudaria a UX conhecida), rola por dentro.
							maxHeight: `calc(100vh - ${rect.bottom + 12}px)`,
							overflowY: "auto",
						}}
						className="z-50 w-52 overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
					>
						{/* Emojis de uso frequente na primeira linha: reagir é o gesto
						    mais comum aqui e não deve custar dois cliques. O toque no
						    mesmo emoji desfaz — quem decide isso é o backend. */}
						{podeReagir && !message.isDeleted && (
							<div className="mb-1 border-b pb-1.5">
								<div className="flex items-center justify-between px-1">
									{RAPIDOS.map((emoji) => (
										<button
											key={emoji}
											type="button"
											title={`Reagir com ${emoji}`}
											onClick={() => reagir(emoji)}
											className="rounded-full px-1 text-lg leading-none transition-transform hover:scale-125"
										>
											{emoji}
										</button>
									))}
									<button
										type="button"
										title="Mais emojis"
										aria-label="Mais emojis"
										onClick={(e) => {
											// O rect precisa sair do evento AQUI, síncrono. Se fosse lido
											// dentro do updater do setState, o React (StrictMode) chama essa
											// função de novo para checar pureza — e na segunda chamada o
											// currentTarget do evento sintético já está null.
											const rectBotao = e.currentTarget.getBoundingClientRect();
											setSeletorPos((atual) =>
												atual ? null : posicaoClampada(rectBotao, 280, 435)
											);
										}}
										className="rounded-full px-1 text-muted-foreground hover:scale-125 hover:text-foreground"
									>
										<SmilePlus className="h-[18px] w-[18px]" />
									</button>
								</div>
							</div>
						)}

						<button
							type="button"
							role="menuitem"
							onClick={responder}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Reply className="h-4 w-4" />
							{i18n.t("messageOptionsMenu.reply")}
						</button>

						{podeEditar && (
							<button
								type="button"
								role="menuitem"
								onClick={() => {
									setEditingMessage(message);
									fecharTudo();
								}}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Pencil className="h-4 w-4" />
								Editar
							</button>
						)}

						<button
							type="button"
							role="menuitem"
							onClick={() => {
								setEncaminhar(true);
								fecharTudo();
							}}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Forward className="h-4 w-4" />
							Encaminhar
						</button>

						{message.body && !message.isDeleted && (
							<button
								type="button"
								role="menuitem"
								onClick={copiarTexto}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Copy className="h-4 w-4" />
								Copiar texto
							</button>
						)}

						{message.mediaUrl && !message.isDeleted && (
							<button
								type="button"
								role="menuitem"
								onClick={baixarMidia}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<Download className="h-4 w-4" />
								Baixar mídia
							</button>
						)}

						{podeCopiarImagem && !message.isDeleted && (
							<button
								type="button"
								role="menuitem"
								onClick={copiarImagem}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
							>
								<ImageIcon className="h-4 w-4" />
								Copiar imagem
							</button>
						)}

						<button
							type="button"
							role="menuitem"
							onClick={(e) => {
								const rectBotao = e.currentTarget.getBoundingClientRect();
								setInfoPos((atual) =>
									atual ? null : posicaoClampada(rectBotao, 224, 90)
								);
							}}
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
						>
							<Info className="h-4 w-4" />
							Informações
						</button>

						{message.fromMe && (
							<button
								type="button"
								role="menuitem"
								onClick={abrirConfirmacao}
								className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
							>
								<Trash2 className="h-4 w-4" />
								{i18n.t("messageOptionsMenu.delete")}
							</button>
						)}
					</div>,
					document.body
				)}

			{/* Portal à parte: o menu principal tem overflow-hidden (para os
			    cantos arredondados) e só 176px de largura, e o seletor completo
			    de emoji é bem maior que isso — dentro dele ficaria cortado. */}
			{seletorPos &&
				createPortal(
					<div
						ref={seletorRef}
						style={{ position: "fixed", ...seletorPos }}
						className="z-50 max-w-full overflow-hidden rounded-lg shadow-lg"
					>
						<Picker
							data={emojiData}
							locale="pt"
							perLine={7}
							maxFrequentRows={1}
							previewPosition="none"
							skinTonePosition="none"
							theme={darkMode ? "dark" : "light"}
							onEmojiSelect={(e) => reagir(e.native)}
						/>
					</div>,
					document.body
				)}

			{infoPos &&
				createPortal(
					<div
						ref={infoRef}
						role="dialog"
						aria-label="Informações da mensagem"
						style={{ position: "fixed", ...infoPos }}
						className="z-50 w-56 rounded-md border bg-popover p-2 text-xs text-popover-foreground shadow-md"
					>
						<p>
							<span className="text-muted-foreground">Enviada em: </span>
							{format(parseISO(message.createdAt), "dd/MM/yyyy 'às' HH:mm:ss")}
						</p>
						<p className="mt-1">
							<span className="text-muted-foreground">Status: </span>
							{rotuloDoAck(message)}
						</p>
					</div>,
					document.body
				)}
		</>
	);
};

export default MessageOptionsMenu;
